import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Alert, Box, Text, useMantineTheme } from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { useIntl } from "react-intl";

import { isEmbedPath } from "../../embed/isEmbedPath";
import { useRemoteAssetsRoutes } from "../../embed/routes/useRemoteAssetsRoutes";
import { SearchPanel } from "../../shared/components/search-panel/SearchPanel";
import { useSelectedOrganizationContainer } from "../../shared/hooks";
import { messages } from "../hooks/messages.generated";
import { useBulkUpdateForking } from "../hooks/useBulkUpdateForking";
import { useDeleteRemoteAssets } from "../hooks/useDeleteRemoteAssets";
import { useMutationStatus } from "../hooks/useMutationStatus";
import { useRemoteAssets } from "../hooks/useRemoteAssets";
import { useRemoteAssetsQuota } from "../hooks/useRemoteAssetsQuota";

import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { RemoteAssetsCardList } from "./RemoteAssetsCardList";
import { emptyState, heading, page, searchPanelFill } from "./RemoteAssetsPage.module.css";
import { RemoteAssetsQuotaBanner } from "./RemoteAssetsQuotaBanner";
import { RemoteAssetsTable } from "./RemoteAssetsTable";
import { type RemoteAssetsMode, RemoteAssetsToolbar } from "./RemoteAssetsToolbar";
import { LoadingAssets, NoAssetsFound, NoAssetsInOrg, RemoteAssets } from "./messages.generated";

const DEBOUNCE_MS = 300;

export const RemoteAssetsPage: FC = () => {
    const { orgId } = useSelectedOrganizationContainer();
    const { pathname } = useLocation();
    const isEmbed = isEmbedPath(pathname);
    const navigate = useNavigate();
    const routes = useRemoteAssetsRoutes();
    const intl = useIntl();
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`, false, {
        getInitialValueInEffect: false,
    });

    const [mode, setMode] = useState<RemoteAssetsMode>("normal");
    const modeRef = useRef(mode);
    modeRef.current = mode;
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [searchInput, setSearchInput] = useState("");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [debouncedSearch] = useDebouncedValue(searchInput, DEBOUNCE_MS);

    const [mutationStatus, setMutationStatus] = useMutationStatus();
    const isConfirming = mutationStatus.type === "pending";

    const { assets, isLoading, hasMore, isFetchingMore, loadMore } = useRemoteAssets(debouncedSearch);
    const { quota } = useRemoteAssetsQuota();

    const { deleteAssets } = useDeleteRemoteAssets(setMutationStatus);
    const { bulkUpdateForking } = useBulkUpdateForking(setMutationStatus);

    const [originalForkingAllowed, setOriginalForkingAllowed] = useState<Map<string, boolean>>(new Map());

    const enterDeleteMode = useCallback(() => {
        if (isConfirming) return;
        setMode("delete");
        setCheckedIds(new Set());
    }, [isConfirming]);

    const enterEditCopyingMode = useCallback(() => {
        if (isConfirming) return;
        setMode("editCopying");
        const initial = new Map<string, boolean>();
        const initialChecked = new Set<string>();
        for (const asset of assets) {
            const allowed = asset.forkingAllowed;
            initial.set(asset.id, allowed);
            if (allowed) initialChecked.add(asset.id);
        }
        setOriginalForkingAllowed(initial);
        setCheckedIds(initialChecked);
    }, [assets, isConfirming]);

    const exitMode = useCallback(() => {
        setMode("normal");
        setCheckedIds(new Set());
        setOriginalForkingAllowed(new Map());
    }, []);

    const toggleExpand = useCallback((id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleChecked = useCallback(
        (id: string) => {
            if (isConfirming) return;
            setCheckedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        },
        [isConfirming],
    );

    const toggleAll = useCallback(() => {
        if (isConfirming) return;
        const eligibleAssets = mode === "delete" ? assets.filter((asset) => asset.lensUsages === 0) : assets;
        const allChecked = eligibleAssets.every((asset) => checkedIds.has(asset.id));

        setCheckedIds((prev) => {
            const next = new Set(prev);
            for (const asset of eligibleAssets) {
                if (allChecked) next.delete(asset.id);
                else next.add(asset.id);
            }
            return next;
        });
    }, [assets, checkedIds, isConfirming, mode]);

    const handleConfirmDeleteFromModal = useCallback(async () => {
        const ids = [...checkedIds];
        if (ids.length === 0) return;
        setDeleteModalOpen(false);
        const success = await deleteAssets(ids);
        if (success) exitMode();
    }, [checkedIds, deleteAssets, exitMode]);

    const confirmCopyingChanges = useCallback(async () => {
        const enableIds: string[] = [];
        const disableIds: string[] = [];

        for (const [id, wasAllowed] of originalForkingAllowed) {
            const isNowAllowed = checkedIds.has(id);
            if (isNowAllowed && !wasAllowed) enableIds.push(id);
            if (!isNowAllowed && wasAllowed) disableIds.push(id);
        }

        if (enableIds.length === 0 && disableIds.length === 0) {
            exitMode();
            return;
        }

        const success = await bulkUpdateForking({ enableIds, disableIds });
        if (success) exitMode();
    }, [originalForkingAllowed, checkedIds, bulkUpdateForking, exitMode]);

    const actionCount = useMemo(() => {
        if (mode === "delete") return checkedIds.size;
        if (mode === "editCopying") {
            let count = 0;
            for (const [id, wasAllowed] of originalForkingAllowed) {
                if (checkedIds.has(id) !== wasAllowed) count++;
            }
            return count;
        }
        return 0;
    }, [mode, checkedIds, originalForkingAllowed]);

    const handleRowClick = useCallback(
        (assetId: string) => {
            navigate(routes.assetDetail(orgId ?? "", assetId), { state: { fromList: true } });
        },
        [navigate, orgId, routes],
    );

    useEffect(() => {
        if (mode !== "editCopying") return;

        const newAllowedIds: string[] = [];
        let hasNew = false;

        for (const asset of assets) {
            if (!originalForkingAllowed.has(asset.id)) {
                hasNew = true;
                if (asset.forkingAllowed) {
                    newAllowedIds.push(asset.id);
                }
            }
        }

        if (!hasNew) return;

        setOriginalForkingAllowed((prev) => {
            const next = new Map(prev);
            for (const asset of assets) {
                if (!next.has(asset.id)) {
                    next.set(asset.id, asset.forkingAllowed);
                }
            }
            return next;
        });

        if (newAllowedIds.length > 0) {
            setCheckedIds((prevChecked) => {
                const nextChecked = new Set(prevChecked);
                for (const id of newAllowedIds) {
                    nextChecked.add(id);
                }
                return nextChecked;
            });
        }
    }, [assets, mode, originalForkingAllowed]);

    useEffect(() => {
        if (modeRef.current !== "normal") {
            exitMode();
        }
        setExpandedIds(new Set());
    }, [debouncedSearch, exitMode]);

    let listContent: ReactNode;
    if (isLoading && assets.length === 0) {
        listContent = (
            <Box className={emptyState}>
                <Text c="dimmed">
                    <LoadingAssets />
                </Text>
            </Box>
        );
    } else if (assets.length === 0) {
        listContent = (
            <Box className={emptyState}>
                <Text c="dimmed">{debouncedSearch ? <NoAssetsFound /> : <NoAssetsInOrg />}</Text>
            </Box>
        );
    } else if (isMobile) {
        listContent = null;
    } else {
        listContent = (
            <RemoteAssetsTable
                assets={assets}
                mode={mode}
                checkedIds={checkedIds}
                onToggleChecked={toggleChecked}
                onToggleAll={toggleAll}
                onRowClick={handleRowClick}
                originalForkingAllowed={originalForkingAllowed}
                hasMore={hasMore}
                isFetchingMore={isFetchingMore}
                onLoadMore={() => {
                    void loadMore();
                }}
                orgId={orgId ?? ""}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
            />
        );
    }

    return (
        <Box className={page}>
            {!isEmbed && (
                <Text size="xl" fw={700} className={heading}>
                    <RemoteAssets />
                </Text>
            )}

            {mutationStatus.type === "error" && (
                <Alert
                    color="red"
                    variant="light"
                    withCloseButton
                    onClose={() => {
                        setMutationStatus({ type: "idle" });
                    }}
                >
                    {mutationStatus.message}
                </Alert>
            )}

            {quota && quota.totalBytes > 0 && (
                <RemoteAssetsQuotaBanner
                    usedBytes={quota.usedBytes}
                    totalBytes={quota.totalBytes}
                    maxAssetSize={quota.perAssetLimitBytes}
                    maxLensSize={quota.perLensLimitBytes}
                />
            )}

            <SearchPanel
                className={searchPanelFill}
                searchPlaceholder={intl.formatMessage(messages.searchAssetsPlaceholder)}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                actions={
                    <RemoteAssetsToolbar
                        mode={mode}
                        actionCount={actionCount}
                        onEnterDeleteMode={enterDeleteMode}
                        onEnterEditCopyingMode={enterEditCopyingMode}
                        onCancel={exitMode}
                        onConfirmDelete={() => {
                            setDeleteModalOpen(true);
                        }}
                        onConfirmCopyingChanges={() => {
                            void confirmCopyingChanges();
                        }}
                        isConfirming={isConfirming}
                        isMobile={isMobile}
                    />
                }
                isLoading={isLoading}
            >
                {listContent}
            </SearchPanel>

            {isMobile && assets.length > 0 && (
                <RemoteAssetsCardList
                    assets={assets}
                    mode={mode}
                    checkedIds={checkedIds}
                    onToggleChecked={toggleChecked}
                    onRowClick={handleRowClick}
                    originalForkingAllowed={originalForkingAllowed}
                    hasMore={hasMore}
                    isFetchingMore={isFetchingMore}
                    onLoadMore={() => {
                        void loadMore();
                    }}
                    orgId={orgId ?? ""}
                    expandedIds={expandedIds}
                    onToggleExpand={toggleExpand}
                />
            )}

            <DeleteConfirmModal
                opened={deleteModalOpen}
                onConfirm={() => {
                    void handleConfirmDeleteFromModal();
                }}
                onCancel={() => {
                    setDeleteModalOpen(false);
                }}
                count={checkedIds.size}
                isDeleting={isConfirming}
            />
        </Box>
    );
};
